<script lang="ts">
    import { Button as CarbonButton, FormGroup, FluidForm, ButtonSet } from "carbon-components-svelte";
    import { TextInput } from "carbon-components-svelte";
    import { MultiSelect } from "carbon-components-svelte";
    import TrashCan32 from "carbon-icons-svelte/lib/TrashCan32";
    import Save32 from "carbon-icons-svelte/lib/Save32";

    import { onDestroy } from 'svelte';
    import stringify from 'fast-json-stable-stringify'

    import InlinePasswordInput from './InlinePasswordInput.svelte'
    import Dropdown from "./Dropdown.svelte";

    import {actor_groups, role_list, streamer_list, streamer_groups, actor_list, diffArray, newId, job_names, isBuiltin} from "./jmdb"
    import type {Actor} from "./jmdb"
    import RightsEditor from './RightsEditor.svelte';


    export let actor: Actor

    let refetchRelevant = (): Promise<any> => {
        return Promise.all([
            actor_groups.fetch(),
            streamer_list.fetch(),
            streamer_groups.fetch(),
            actor_list.fetch(),
        ])
    }

    let oldActor: Actor = JSON.parse(JSON.stringify(actor))
    actor.email = actor.email || ''

    let new_password = ''

    let email_validator = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i
    let in_flight = false
    let rightsEditor: RightsEditor

    let email_invalid = false
    let revalidate_email = function() {
        email_invalid = actor.email.match(email_validator) == null
    }

    let name_dirty = false
    $: name_dirty = actor.name != oldActor.name
    let email_dirty = false
    $: email_dirty = actor.email != oldActor.email
    let role_dirty = false
    $: role_dirty = actor.role != oldActor.role
    let password_dirty = false
    $: password_dirty = (new_password != "")
    let job_dirty = false
    $: job_dirty = actor.job != oldActor.job
    let special_dirty = false
    $: special_dirty = stringify(actor.specialRights) != stringify(oldActor.specialRights)
    
    let dirty = false
    
    let old_role = actor.role
    $: role_dirty = actor.role != old_role 
    $: dirty = name_dirty || email_dirty || groups_dirty || role_dirty || password_dirty || job_dirty || special_dirty
    let actorGroups: string[] = []
    let oldActorGroups: string[] = []
    onDestroy(actor_groups.subscribe((val) => {
        if (!val.ready) {
            return
        }
        if (actorGroups.length != 0) {
            return
        }
        actorGroups = []
        for (let gidx in val.data) {
            let g = val.data[gidx]
            if (g.actors.indexOf(actor.id) != -1) {
                actorGroups.push(g.id.toString())
            }
        }
        actorGroups = actorGroups
        oldActorGroups = actorGroups.slice()
    }))

    let groups_dirty = false
    $: groups_dirty = stringify(oldActorGroups) != stringify(actorGroups)

    let built_in = false
    $: built_in = isBuiltin(actor.id)



    let after_changes = (res: Response) => {
        if (!res.ok) {
            res.text().then((txt) => {
                window.alert(txt)
            })
            in_flight = false
        } else {
            actor_list.invalidate()
            actor_groups.invalidate()
            refetchRelevant().then(() => {
                in_flight = false
            })
        }
    }
    let saveClicked = () => {
        let old = oldActor
        let groupChanges = diffArray(oldActorGroups, actorGroups, (x)=>x, false)

        let changeBundle: any = {
            newPassword: new_password,
            memberChanges: groupChanges
        }
        const matchedFields = ["name", "desc", "email", "job", "role"]
        matchedFields.forEach(el => {
            if ((old as any)[el] != (actor as any)[el]) {
                changeBundle[el] = (actor as any)[el]
            }
        })
        rightsEditor.addModifications(changeBundle, old.specialRights)

        // Always include job on new actors
        if (actor.id == newId) {
            changeBundle.job = actor.job
        }
        fetch("/api/v1/actors/"+actor.id, {
            'method': 'POST',
            'body': JSON.stringify(changeBundle)
        }).then(after_changes)
        in_flight = true
    }

    let deleteActor = (e: Event) => {
        e.preventDefault();
        if (window.confirm("Are you sure you want to delete "+actor.name+"?")) {
            fetch("/api/v1/actors/"+actor.id, {
                'method': 'DELETE',
                'body': JSON.stringify({})
            }).then(after_changes)
        }
    }
    refetchRelevant()

</script>

<style>
    section div {
        margin: 1em 0 .6em;
    }

    @media (min-width: 66rem) {
        .input-fields-container {
            display: flex;
            flex-wrap: wrap;
            margin: -1rem;
        }
    }

    

</style>


    <section>

        <FormGroup>
            <TextInput bind:value={actor.name} labelText="Name" disabled={built_in} placeholder="Enter person's display name"/>
        
    <TextInput
    type="email"
    on:keydown={()=>{email_invalid = false}}
    on:blur={revalidate_email}
    bind:value={actor.email}
    labelText="Email"
    placeholder="Enter the email associated with this person"
    invalid={email_invalid}
    invalidText="That's not a valid email address."
    />
        <InlinePasswordInput bind:value={new_password} labelText="Change password" placeholder="To change the password, enter a new password here"/>
        </FormGroup>

    <FormGroup disabled={built_in}>
        <div class="input-fields-container">
        <Dropdown disabled={built_in} class="input-field-flex-half" titleText="Job" bind:selectedVal={actor.job}
            items={Object.keys(job_names).map((k)=>({id: k, val: k, text: job_names[k]}))}/>

        {#if $role_list.ready}
            <Dropdown disabled={built_in} class="input-field-flex-half" titleText="Role" bind:selectedVal={actor.role}
            items={$role_list.data.map((v) => ({id: v.id.toString(), val: v.id, text: v.name}))}/>
        {/if}

            {#if $actor_groups.ready}
            <MultiSelect disabled={built_in} class="input-field-flex-full"
                titleText="Member of:"
                label={actorGroups.map(v => $actor_groups.data.find(ag => ag.id.toString() == v) || {'name': "Not found"}).map(ag=>ag.name).join(', ') || "No groups"}
                items={$actor_groups.data.map(v => { return {id: v.id.toString(), val: v.id, text: v.name}})}
                bind:selectedIds={actorGroups}
                />
                

            {/if}
        </div>
    </FormGroup>

    <FormGroup disabled={built_in}>
        <h4 class="bx--data-table-header__title">Special rights:</h4>
        <RightsEditor bind:this={rightsEditor} bind:rights={actor.specialRights}/>
    </FormGroup>

    <ButtonSet stacked style="float: right;">
        <CarbonButton kind="primary" icon={Save32} disabled={!dirty || in_flight} on:click={saveClicked}>Save</CarbonButton>
        {#if actor.id != newId}
        <CarbonButton disabled={built_in} kind="danger-ghost" icon={TrashCan32} tooltipPosition="left" on:click={deleteActor}>Delete</CarbonButton>
        {/if}
    </ButtonSet>
    
</section>
